import { v4 as uuid } from 'uuid';
import {
  ParticipantFieldType,
  FieldAnswer,
  Participant,
  ParticipantField,
  Criterion,
  ListValueType,
} from '@app/api';
import moment from 'moment';
import { TFunction } from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type BuildFunction = (
  field: ParticipantField,
  participant: Partial<Participant>,
  criterion: Criterion,
  t: TFunction,
) => Omit<FieldAnswer, 'field'> | undefined;

const defaultBuild: BuildFunction = (field, participant) => {
  const rawValue = participant[field.name as keyof Participant];
  return {
    type: 'text',
    text: String(rawValue),
  };
};

const choiceBuild: BuildFunction = (field, participant, criterion, t) => {
  const rawValue = participant[field.name as keyof Participant];
  if (Array.isArray(rawValue)) {
    return multipleChoiceBuild(field, participant, criterion, t);
  }

  if (!rawValue) {
    return undefined;
  }
  const choice = (field.choices || []).find(
    (choice) => choice.value === rawValue,
  );
  if (!choice) {
    return undefined;
  }

  return {
    type: 'choice',
    choice: {
      label: t(choice.label),
      ref: String(choice.value),
      value: String(choice.value),
    },
  };
};

const multipleChoiceBuild: BuildFunction = (
  field,
  participant,
  criterion,
  t,
) => {
  const rawValue = participant[field.name as keyof Participant];
  if (!rawValue) {
    return undefined;
  }
  if (!Array.isArray(rawValue)) {
    return choiceBuild(field, participant, criterion, t);
  }
  const strValues = rawValue.map((val) => String(val));

  const choices = (field.choices || []).filter((choice) =>
    strValues.includes(String(choice.value)),
  );

  const values = choices.map((choice) => String(choice.value));

  return {
    type: 'choices',
    choices: {
      labels: choices.map((choice) => t(choice.label)),
      refs: values,
      values: values,
    },
  };
};

const dateBuild: BuildFunction = (field, participant, criterion, t) => {
  const rawValue = participant[field.name as keyof Participant];
  if (!rawValue) {
    return undefined;
  }
  if (!(rawValue instanceof Date || typeof rawValue === 'string')) {
    return defaultBuild(field, participant, criterion, t);
  }

  const date = moment(rawValue);
  if (!date.isValid()) {
    return defaultBuild(field, participant, criterion, t);
  }

  if (criterion.listValueType === ListValueType.AGE_RANGE) {
    return {
      type: 'number',
      number: moment().diff(date, 'year'),
    };
  }

  return {
    type: 'date',
    date: date.format(),
  };
};

const buildByType: Record<ParticipantFieldType, BuildFunction> = {
  [ParticipantFieldType.EMAIL]: defaultBuild,
  [ParticipantFieldType.PHONE_NUMBER]: defaultBuild,
  [ParticipantFieldType.SINGLE_CHOICE]: choiceBuild,
  [ParticipantFieldType.MULTIPLE_CHOICE]: multipleChoiceBuild,
  [ParticipantFieldType.TAGS]: defaultBuild,
  [ParticipantFieldType.SHORT_TEXT]: defaultBuild,
  [ParticipantFieldType.LONG_TEXT]: defaultBuild,
  [ParticipantFieldType.DATE]: dateBuild,
  [ParticipantFieldType.NUMBER]: defaultBuild,
  [ParticipantFieldType.GEOLOCATION]: defaultBuild,
  [ParticipantFieldType.INCOME]: choiceBuild,
  [ParticipantFieldType.TIMEZONE]: defaultBuild,
  [ParticipantFieldType.LOCALES]: multipleChoiceBuild,
  [ParticipantFieldType.BOOLEAN]: defaultBuild,
};

export const useBuildAnswerForParticipantField = () => {
  const { t } = useTranslation();

  return useCallback(
    (
      field: ParticipantField,
      participant: Partial<Participant>,
      criterion: Criterion,
    ): FieldAnswer | undefined => {
      const type = field.type;
      const builder = buildByType[type] || defaultBuild;

      const partial = builder(field, participant, criterion, t);
      if (!partial) {
        return undefined;
      }
      return {
        field: {
          id: uuid(),
          ref: field.name,
          title: t(field.label, { context: 'answer' }),
          type: field.type,
        },
        ...partial,
      };
    },
    [t],
  );
};
